// views/Dashboard/Profile/components/FichasDisponiblesColumn.js
import React from 'react';
import {
  Box,
  VStack,
  Text,
  Icon,
  Tooltip,
  IconButton,
  HStack
} from '@chakra-ui/react';
import {
  FileText,
  Calendar,
  ArrowRight,
  Eye
} from 'lucide-react';
import { casosUtils } from 'services/casosService';

const CATEGORY_STYLE = {
  casos:       { bg: 'rose.bg', ink: 'rose.ink' },
  atencion:    { bg: 'sky.bg',  ink: 'sky.ink'  },
  seguimiento: { bg: 'mint.bg', ink: 'mint.ink' },
  soporte:     { bg: 'lav.bg',  ink: 'lav.ink'  },
};
const DEFAULT_CATEGORY_STYLE = { bg: 'cream.bg2', ink: 'ink.400' };
const styleForCategory = (category) => CATEGORY_STYLE[category] || DEFAULT_CATEGORY_STYLE;

const FichasDisponiblesColumn = ({
  fichas = [],
  onDragStart,
  onDragEnd,
  onDrop,
  onDragOver,
  onDragLeave,
  isDragOver = false,
  draggedFicha = null,
  onShowDetail = null
}) => {
  return (
    <Box
      minW="300px"
      w="300px"
      h="100%"
      bg={isDragOver ? 'coral.tint' : 'cream.bg2'}
      border="2px dashed"
      borderColor={isDragOver ? 'coral.500' : 'cream.line2'}
      borderRadius="16px"
      p={4}
      onDrop={(e) => onDrop && onDrop(e, 'disponible')}
      onDragOver={(e) => onDragOver && onDragOver(e, 'disponible')}
      onDragLeave={onDragLeave}
      transition="background .15s, border-color .15s"
      fontFamily="jakarta"
    >
      {/* Header de la columna */}
      <VStack spacing={2} align="stretch" mb={4}>
        <Box textAlign="center">
          <Text fontSize="14.5px" fontWeight="800" color="ink.900">
            Fichas disponibles
          </Text>
          <Box as="span" mt={1} display="inline-block" px="9px" py="2px" borderRadius="999px"
            bg="sky.bg" color="sky.ink" fontSize="11px" fontWeight="700">
            {fichas.length} ficha{fichas.length !== 1 ? 's' : ''}
          </Box>
        </Box>

        <Text fontSize="11px" color="ink.400" textAlign="center" fontStyle="italic">
          Arrastra fichas hacia "Recepción" para asociarlas al caso
        </Text>
      </VStack>

      {/* Lista de fichas disponibles */}
      <VStack spacing={2} align="stretch" maxH="500px" overflowY="auto">
        {fichas.length === 0 ? (
          <Box bg="white" borderRadius="12px" border="1px solid" borderColor="cream.line" p={4} textAlign="center">
            <Icon as={FileText} boxSize={7} color="ink.400" mb={2} />
            <Text fontSize="12.5px" color="ink.400" fontWeight="600">
              No hay fichas disponibles
            </Text>
            <Text fontSize="11px" color="ink.400">
              Todas las fichas están asignadas a casos
            </Text>
          </Box>
        ) : (
          fichas.map((ficha) => (
            <FichaCard
              key={ficha.id}
              ficha={ficha}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              isDragging={draggedFicha?.id === ficha.id}
              isAvailable={true}
              onShowDetail={onShowDetail}
            />
          ))
        )}
      </VStack>
    </Box>
  );
};

// Componente para cada ficha individual
const FichaCard = ({
  ficha,
  onDragStart,
  onDragEnd,
  isDragging = false,
  isAvailable = false,
  onShowDetail = null
}) => {
  const categoryStyle = styleForCategory(ficha.survey_category);
  const opacity = isDragging ? 0.5 : 1;

  return (
    <Box
      bg="white"
      border="1px solid" borderColor="cream.line"
      borderRadius="12px"
      p={3}
      cursor="grab"
      opacity={opacity}
      transform={isDragging ? 'rotate(5deg)' : 'none'}
      transition="background .13s, border-color .13s, transform .13s"
      _hover={{
        bg: 'coral.tint', borderColor: 'coral.soft',
        transform: isDragging ? 'rotate(5deg)' : 'translateY(-2px)',
      }}
      _active={{ cursor: 'grabbing' }}
      draggable
      onDragStart={(e) => onDragStart && onDragStart(e, ficha)}
      onDragEnd={onDragEnd}
    >
      <VStack align="stretch" spacing={2}>
        {/* Header con categoría y estado */}
        <HStack justify="space-between" align="center">
          <Box as="span" px="8px" py="2px" borderRadius="999px" bg={categoryStyle.bg} color={categoryStyle.ink}
            fontSize="10.5px" fontWeight="700">
            {ficha.survey_category || 'sin categoría'}
          </Box>

          {isAvailable && (
            <Tooltip label="Disponible para asignar">
              <Box as="span" px="8px" py="2px" borderRadius="999px" border="1px solid" borderColor="mint.ink"
                color="mint.ink" fontSize="10.5px" fontWeight="700">
                Disponible
              </Box>
            </Tooltip>
          )}
        </HStack>

        {/* Título de la ficha */}
        <Text fontSize="13px" fontWeight="700" color="ink.900" noOfLines={2}>
          {ficha.survey_title || 'Sin título'}
        </Text>

        {/* Información adicional */}
        <VStack spacing={1} align="stretch">
          <HStack fontSize="11px" color="ink.400">
            <Icon as={Calendar} boxSize={3} />
            <Text>{casosUtils.getTimeAgo(ficha.created_at)}</Text>
          </HStack>

          {/* Acciones */}
          <HStack justify="space-between" mt={1}>
            <HStack fontSize="10.5px" color="coral.500" fontStyle="italic" spacing={1}>
              <Text>Arrastra para asignar</Text>
              <Icon as={ArrowRight} boxSize={3} />
            </HStack>

            {/* Botón ver detalles */}
            {onShowDetail && (
              <Tooltip label="Ver detalles" hasArrow>
                <IconButton
                  icon={<Icon as={Eye} boxSize="13px" />}
                  size="xs" variant="ghost" color="ink.400"
                  _hover={{ bg: 'coral.soft', color: 'coral.700' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowDetail(ficha);
                  }}
                  aria-label="Ver detalles"
                />
              </Tooltip>
            )}
          </HStack>
        </VStack>
      </VStack>
    </Box>
  );
};

export default FichasDisponiblesColumn;
